'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'
import { teamKeys } from '@/hooks/api/query-keys'
import { useInvalidatePermissions, usePermissions } from '@/hooks/use-permissions'
import type { RoleCatalogue, WorkspaceTier } from './permission-areas'
import { BUILT_IN_ROLE_KEYS, type BuiltInRoleKey } from './role-artwork'

/**
 * Settings → Roles and permissions: `GET/POST/PATCH/DELETE /v1/roles` and
 * `GET /v1/roles/catalogue` (apps/api/src/modules/roles/roles.controller.ts).
 *
 * The hooks hand the screens ARRAYS, always — while loading and when the answer leaves a list
 * out — so no view needs a `?? []` (apps/web/CLAUDE.md §1). The bodies are normalised in
 * `select`, by the stable functions below.
 *
 * Keys follow the ESA pattern (CLAUDE.md §1b): no workspace id in any key. The list counts the
 * built-in roles' holders in the CURRENT workspace, and that stays right because
 * WorkspaceCacheGuard clears the cache on a workspace or company switch and fetchApi sends the
 * `x-workspace-id` header. Every change also invalidates the team list (it shows each member's
 * custom role) and the signed-in person's own grants (an edited role applies to its holders at
 * once).
 */

export type { RoleCatalogue, WorkspaceTier } from './permission-areas'

export interface BuiltInRole {
  kind: 'built-in'
  key: BuiltInRoleKey
  /** `users.role` label, e.g. "Workspace Admin". */
  userRole: string
  workspaceRole: WorkspaceTier
  artworkKey: string
  permissions: string[]
  memberCount: number
}

export interface CustomRole {
  kind: 'custom'
  id: string
  name: string
  description: string
  artworkKey: string | null
  baseTier: WorkspaceTier
  permissions: string[]
  /** The people holding it, in the company's live workspaces (contract C3). */
  memberCount: number
  createdAt: string
  updatedAt: string
}

export interface RoleList {
  builtIn: BuiltInRole[]
  custom: CustomRole[]
}

export interface RoleInput {
  name: string
  description?: string
  artworkKey?: string | null
  permissions: string[]
}

export const roleKeys = {
  all: () => ['roles'] as const,
  list: () => [...roleKeys.all(), 'list'] as const,
  catalogue: () => [...roleKeys.all(), 'catalogue'] as const,
}

// ── Normalising what the API sends ───────────────────────────────────────────

type Body = Record<string, unknown>

const objectOf = (value: unknown): Body => (value && typeof value === 'object' ? (value as Body) : {})
/** `{ data: … }` or the bare value. */
const unwrap = (body: unknown): Body => {
  const outer = objectOf(body)
  return 'data' in outer ? objectOf(outer.data) : outer
}
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
const text = (value: unknown): string => (typeof value === 'string' ? value : '')
const textOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null)
const count = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}
const TIERS: readonly WorkspaceTier[] = ['Admin', 'Editor', 'Viewer']
const tier = (value: unknown): WorkspaceTier =>
  TIERS.find((t) => t === value) ?? 'Viewer'

function normaliseBuiltIn(raw: unknown): BuiltInRole | null {
  const r = objectOf(raw)
  const key = BUILT_IN_ROLE_KEYS.find((k) => k === r.key)
  if (!key) return null
  return {
    kind: 'built-in',
    key,
    userRole: text(r.userRole),
    workspaceRole: tier(r.workspaceRole),
    artworkKey: text(r.artworkKey),
    permissions: strings(r.permissions),
    memberCount: count(r.memberCount),
  }
}

function normaliseCustom(raw: unknown): CustomRole | null {
  const r = objectOf(raw)
  if (typeof r.id !== 'string') return null
  return {
    kind: 'custom',
    id: r.id,
    name: text(r.name),
    description: text(r.description),
    artworkKey: textOrNull(r.artworkKey),
    baseTier: tier(r.baseTier),
    permissions: strings(r.permissions),
    memberCount: count(r.memberCount),
    createdAt: text(r.createdAt),
    updatedAt: text(r.updatedAt),
  }
}

const isRole = <T,>(role: T | null): role is T => role !== null

/** `GET /v1/roles`, whatever the shape of the answer. */
export function normaliseRoleList(body: unknown): RoleList {
  const list = unwrap(body)
  return {
    builtIn: (Array.isArray(list.builtIn) ? list.builtIn : []).map(normaliseBuiltIn).filter(isRole),
    custom: (Array.isArray(list.custom) ? list.custom : []).map(normaliseCustom).filter(isRole),
  }
}

/** `GET /v1/roles/catalogue` (C1), whatever the shape of the answer. */
export function normaliseCatalogue(body: unknown): RoleCatalogue {
  const c = unwrap(body)
  return {
    actions: strings(c.actions),
    subjects: strings(c.subjects),
    grantable: strings(c.grantable),
    baseline: strings(c.baseline),
    editorTier: strings(c.editorTier),
  }
}

const NO_BUILT_IN_ROLES: BuiltInRole[] = []
const NO_CUSTOM_ROLES: CustomRole[] = []
const NO_GRANTS: string[] = []
export const EMPTY_ROLE_CATALOGUE: RoleCatalogue = {
  actions: [],
  subjects: [],
  grantable: [],
  baseline: [],
  editorTier: [],
}

// ── Reading ─────────────────────────────────────────────────────────────────

/** The built-in and custom roles. `enabled: false` where the viewer cannot read roles (a 403). */
export function useRoles(options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: roleKeys.list(),
    enabled: options.enabled ?? true,
    queryFn: () => fetchApi<unknown>('/roles'),
    select: normaliseRoleList,
  })
  return {
    builtIn: query.data?.builtIn ?? NO_BUILT_IN_ROLES,
    custom: query.data?.custom ?? NO_CUSTOM_ROLES,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * What a custom role may be built from (C1) — fetched once per session. `ready` once it holds what
 * the editor needs: the subjects and the grants a custom role may have.
 */
export function useRoleCatalogue(options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: roleKeys.catalogue(),
    enabled: options.enabled ?? true,
    queryFn: () => fetchApi<unknown>('/roles/catalogue'),
    select: normaliseCatalogue,
    staleTime: Infinity,
  })
  const catalogue = query.data ?? EMPTY_ROLE_CATALOGUE
  return {
    catalogue,
    ready: catalogue.subjects.length > 0 && catalogue.grantable.length > 0,
    isError: query.isError,
  }
}

/** The signed-in person's own grants here: an empty list until they are known. */
export function useMyGrants(): readonly string[] {
  const { data } = usePermissions()
  return data?.permissions ?? NO_GRANTS
}

// ── Changing ────────────────────────────────────────────────────────────────

function useInvalidateRoles() {
  const queryClient = useQueryClient()
  // The grants live in the ROOT client (usePermissions), which a module's own QueryProvider hides.
  const invalidatePermissions = useInvalidatePermissions()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: roleKeys.all() }),
      queryClient.invalidateQueries({ queryKey: teamKeys.all }),
      invalidatePermissions(),
    ])
}

export function useCreateRole() {
  const invalidate = useInvalidateRoles()
  return useMutation({
    mutationFn: async (input: RoleInput) =>
      normaliseCustom(unwrap(await fetchApi<unknown>('/roles', { method: 'POST', body: JSON.stringify(input) }))),
    onSuccess: invalidate,
  })
}

export function useUpdateRole() {
  const invalidate = useInvalidateRoles()
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<RoleInput> & { id: string }) =>
      normaliseCustom(
        unwrap(await fetchApi<unknown>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(input) })),
      ),
    onSuccess: invalidate,
  })
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles()
  return useMutation({
    mutationFn: (id: string) => fetchApi<unknown>(`/roles/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}
